import { Component } from '@theme/component';
import { DialogCloseEvent } from '@theme/dialog';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  /** @type {(() => void) | null} */
  #unregisterOverlay = null;

  /** @type {number} */
  #transitionSequence = 0;

  /** @type {number | null} */
  #closeFallbackTimer = null;

  #desiredOpen = false;

  /** @type {{ modal: HTMLElement & { showDialog: () => void, closeDialog: () => Promise<void> }, dialog: HTMLDialogElement, trigger: HTMLElement } | null} */
  #activeModal = null;

  connectedCallback() {
    super.connectedCallback();

    this.#desiredOpen = this.refs.details.hasAttribute('open');
    this.#setupAnimatedElementListeners();
    if (this.#desiredOpen) {
      // Adopt an already used native disclosure without replaying its entrance.
      const scrollTop = this.refs.menuDrawer.scrollTop;
      this.refs.details.setAttribute('data-native-menu-open', '');
      this.open();
      this.refs.menuDrawer.scrollTop = scrollTop;
      if (this.refs.details.hasAttribute('scroll-lock')) document.documentElement.setAttribute('scroll-lock', '');
    }
    customElements.whenDefined('header-component').then(() => {
      if (!this.isConnected) return;
      this.#registerOverlay();
      if (this.#desiredOpen && this.refs.details.open) {
        this.headerComponent?.claimOverlay?.(this, {
          panel: this.refs.menuDrawer,
          trigger: this.refs.details.querySelector('summary'),
        });
        this.headerComponent?.syncOverlayGeometry?.(this);
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.refs.details.hasAttribute('data-native-menu-open')) {
      if ([...document.querySelectorAll('details[open][scroll-lock]')].some((details) => !this.contains(details))) {
        // Another connected disclosure owns the global lock and may own its focus trap.
        reset(this.refs.details);
      } else {
        this.close({ immediate: true, restoreFocus: false });
        // A detached details toggle no longer reaches the document listener.
        if (this.refs.details.hasAttribute('scroll-lock')) document.documentElement.removeAttribute('scroll-lock');
      }
    }
    this.#desiredOpen = false;
    if (this.#closeFallbackTimer !== null) window.clearTimeout(this.#closeFallbackTimer);
    if (this.#activeModal) {
      const activeModal = this.#activeModal;
      void activeModal.modal.closeDialog();
      this.#clearActiveModal(activeModal);
    }
    this.#unregisterOverlay?.();
    this.#unregisterOverlay = null;
  }

  get headerComponent() {
    return this.closest('header-component');
  }

  #registerOverlay() {
    if (this.#unregisterOverlay) return;

    this.#unregisterOverlay = this.headerComponent?.registerOverlay?.(this, {
      id: 'header-drawer',
      getPanel: () => this.refs.menuDrawer,
      getTrigger: () => this.refs.details.querySelector('summary'),
      close: (options = {}) => {
        this.#close(this.refs.details, {
          immediate: options.immediate,
          restoreFocus: options.restoreFocus,
        });
      },
      handleEscape: (event) => {
        if (this.#activeModal) return;

        const details = this.#getDetailsElement(event);
        if (details !== this.refs.details && details.open) {
          this.#close(details);
          return;
        }
        this.#close(this.refs.details, { restoreFocus: true });
      },
    });
  }

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.#desiredOpen;
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   * @param {MouseEvent} event
   */
  toggle(event) {
    event.preventDefault();
    return this.isOpen ? this.close({ restoreFocus: true }) : this.open(undefined, event);
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    if (details === this.refs.details) {
      this.#desiredOpen = true;
      this.refs.menuDrawer.scrollTop = 0;
    }
    const transitionSequence = ++this.#transitionSequence;
    if (this.#closeFallbackTimer !== null) {
      window.clearTimeout(this.#closeFallbackTimer);
      this.#closeFallbackTimer = null;
    }

    event?.preventDefault();
    details.open = true;
    summary.setAttribute('aria-expanded', 'true');

    if (details === this.refs.details) {
      this.#registerOverlay();
      this.headerComponent?.claimOverlay?.(this, { panel: this.refs.menuDrawer, trigger: summary });
    }

    this.preventInitialAccordionAnimations(details);
    details.classList.add('menu-open');

    if (target) {
      this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
    }

    if (details === this.refs.details) this.headerComponent?.syncOverlayGeometry?.(this);

    requestAnimationFrame(() => {
      if (
        transitionSequence !== this.#transitionSequence ||
        !details.open ||
        (details === this.refs.details && !this.#desiredOpen)
      ) {
        return;
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(
        drawer || details,
        () => {
          if (transitionSequence === this.#transitionSequence && details.open && !this.#activeModal) trapFocus(details);
        },
        { subtree: false }
      );
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /** Open Search as a full-screen dialog while preserving the main drawer. */
  openSearch(event) {
    event.preventDefault();

    const searchModal = document.getElementById('search-modal');
    const trigger = this.#getModalTrigger(event, '.menu-drawer__search');
    this.#openModal(searchModal, trigger);
  }

  /** Open localization as a full-screen dialog while preserving the main drawer. */
  openLocalization(event) {
    event.preventDefault();

    const localizationModal = this.querySelector('#HeaderDrawer-localization-modal');
    const trigger = this.#getModalTrigger(event, '#HeaderDrawer-localization');
    this.#openModal(localizationModal, trigger);
  }

  /**
   * Resolve the originating drawer control even when the component action is delegated.
   * @param {Event} event
   * @param {string} fallbackSelector
   * @returns {HTMLElement | null}
   */
  #getModalTrigger(event, fallbackSelector) {
    if (event.currentTarget instanceof HTMLElement && event.currentTarget.matches(fallbackSelector)) {
      return event.currentTarget;
    }

    if (event.target instanceof Element) {
      const trigger = event.target.closest(fallbackSelector);
      if (trigger instanceof HTMLElement) return trigger;
    }

    const fallback = this.querySelector(fallbackSelector);
    return fallback instanceof HTMLElement ? fallback : null;
  }

  /**
   * Suspend only the drawer focus trap while a native top-layer dialog is active.
   * Drawer state, overlay ownership, geometry, and scroll position remain untouched.
   * @param {Element | null} modal
   * @param {HTMLElement | null} trigger
   */
  #openModal(modal, trigger) {
    const dialog = modal instanceof HTMLElement ? modal.querySelector('dialog') : null;

    if (
      !(modal instanceof HTMLElement) ||
      typeof modal.showDialog !== 'function' ||
      typeof modal.closeDialog !== 'function' ||
      !(dialog instanceof HTMLDialogElement) ||
      !(trigger instanceof HTMLElement) ||
      !this.#desiredOpen ||
      !this.refs.details.open ||
      this.#activeModal ||
      dialog.open
    ) {
      return;
    }

    trigger.focus({ preventScroll: true });
    trigger.setAttribute('aria-expanded', 'true');
    removeTrapFocus();

    this.#activeModal = { modal, dialog, trigger };
    dialog.addEventListener('cancel', this.#handleModalCancel);
    modal.addEventListener(DialogCloseEvent.eventName, this.#handleModalClose, { once: true });
    modal.showDialog();
  }

  #handleModalCancel = (event) => {
    const activeModal = this.#activeModal;
    if (!activeModal || event.currentTarget !== activeModal.dialog) return;

    event.preventDefault();
    const { dialog, modal } = activeModal;
    if (dialog.classList.contains('dialog-closing')) return;

    void modal.closeDialog();
  };

  #handleModalClose = (event) => {
    const activeModal = this.#activeModal;
    if (!activeModal || event.currentTarget !== activeModal.modal) return;

    const { trigger } = activeModal;
    this.#clearActiveModal(activeModal);

    if (this.#desiredOpen && this.refs.details.open) {
      if (this.isConnected && window.matchMedia('(max-width: 749px)').matches) {
        trapFocus(this.refs.details);
        if (trigger.isConnected) trigger.focus({ preventScroll: true });
      } else {
        this.#close(this.refs.details, { immediate: true, restoreFocus: false });
      }
    }
  };

  #clearActiveModal(activeModal) {
    if (this.#activeModal !== activeModal) return;

    activeModal.modal.removeEventListener(DialogCloseEvent.eventName, this.#handleModalClose);
    activeModal.dialog.removeEventListener('cancel', this.#handleModalCancel);
    activeModal.trigger.setAttribute('aria-expanded', 'false');
    this.#activeModal = null;
  }

  /**
   * Close the main menu drawer
   */
  close(options = {}) {
    this.#close(this.refs.details, options instanceof Event ? { restoreFocus: true } : options);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   * @param {{ immediate?: boolean, restoreFocus?: boolean }} [options]
   */
  #close(details, options = {}) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    if (details === this.refs.details) {
      this.#desiredOpen = false;
      if (this.#activeModal) {
        const activeModal = this.#activeModal;
        activeModal.trigger.setAttribute('aria-expanded', 'false');
        void activeModal.modal.closeDialog();
        if (!activeModal.dialog.open) this.#clearActiveModal(activeModal);
      }
    }
    const transitionSequence = ++this.#transitionSequence;
    if (this.#closeFallbackTimer !== null) {
      window.clearTimeout(this.#closeFallbackTimer);
      this.#closeFallbackTimer = null;
    }

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    if (options.immediate) {
      reset(details);
      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
        openDetails.forEach(reset);
        this.headerComponent?.releaseOverlay?.(this, { restoreFocus: options.restoreFocus });
      }
      return;
    }

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    let finalized = false;
    const finalize = () => {
      if (
        finalized ||
        transitionSequence !== this.#transitionSequence ||
        (details === this.refs.details && this.#desiredOpen)
      ) {
        return;
      }

      finalized = true;
      if (this.#closeFallbackTimer !== null) {
        window.clearTimeout(this.#closeFallbackTimer);
        this.#closeFallbackTimer = null;
      }

      reset(details);
      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
        openDetails.forEach(reset);
        this.headerComponent?.releaseOverlay?.(this, { restoreFocus: options.restoreFocus });
      } else {
        trapFocus(this.refs.details);
      }
    };

    void onAnimationEnd(drawer || details, finalize, { subtree: false });
    this.#closeFallbackTimer = window.setTimeout(finalize, 350);
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('data-native-menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
